import { useState, useEffect, useCallback } from 'react';
import dashboardService from '../services/dashboardService';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export const useDashboardData = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    matches: 0,
    views: 0,
    favorites: 0,
    unreadMessages: 0
  });
  const [hotMatches, setHotMatches] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [error, setError] = useState(null);
  
  const { user } = useAuth();
  const userId = user?.id || localStorage.getItem('userId');

  // Charger les centres d'intérêt de l'utilisateur
  const loadUserInterests = async () => {
    try {
      const response = await api.get(`/get-profile.php?id=${userId}`);
      if (response.data.success) {
        return response.data.interests || [];
      }
      return [];
    } catch (error) {
      console.error('Erreur chargement intérêts:', error);
      return [];
    }
  };

  // Filtrer les profils par sexe opposé
  const filterByOppositeGender = (profiles) => {
    if (!user?.genre) return profiles;
    
    if (user.genre === 'homme') {
      return profiles.filter(p => p.genre === 'femme');
    } else if (user.genre === 'femme') {
      return profiles.filter(p => p.genre === 'homme');
    }
    return profiles;
  };

  // Calculer la compatibilité avec les intérêts
  const calculateCompatibility = (profile, userInterests) => {
    if (!profile.interests || profile.interests.length === 0) return 0;
    if (userInterests.length === 0) return 50;
    
    const commonInterests = profile.interests.filter(interest => 
      userInterests.some(ui => ui.nom === interest)
    ).length;
    
    return Math.round((commonInterests / userInterests.length) * 100);
  };

  const loadDashboardData = useCallback(async () => {
    if (!userId) {
      setError('Utilisateur non identifié');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const userInterests = await loadUserInterests();
      
      const [
        statsResult,
        matchesResult,
        suggestionsResult,
        visitorsResult,
        photosResult
      ] = await Promise.allSettled([
        dashboardService.getUserStats(userId),
        dashboardService.getHotMatches(userId),
        dashboardService.getSuggestions(userId),
        dashboardService.getRecentVisitors(userId),
        dashboardService.getRecentPhotos()
      ]);

      if (statsResult.status === 'fulfilled' && statsResult.value.success) {
        setStats(statsResult.value.data);
      }

      if (matchesResult.status === 'fulfilled' && matchesResult.value.success) {
        let matches = matchesResult.value.data;
        
        // Filtrer pour exclure l'utilisateur connecté
        matches = matches.filter(match => match.id !== parseInt(userId));
        
        // Filtrer par sexe opposé
        matches = filterByOppositeGender(matches);
        
        // Calculer la compatibilité
        const matchesWithCompatibility = matches.map(match => ({
          ...match,
          compatibility: calculateCompatibility(match, userInterests)
        })).filter(match => match.compatibility > 0);
        
        matchesWithCompatibility.sort((a, b) => b.compatibility - a.compatibility);
        
        setHotMatches(matchesWithCompatibility);
      }

      if (suggestionsResult.status === 'fulfilled' && suggestionsResult.value.success) {
        let suggestions = suggestionsResult.value.data;
        // Filtrer pour exclure l'utilisateur connecté
        suggestions = suggestions.filter(s => s.id !== parseInt(userId));
        setSuggestions(suggestions);
      }

      if (visitorsResult.status === 'fulfilled' && visitorsResult.value.success) {
        let visitors = visitorsResult.value.data;
        // Filtrer pour exclure l'utilisateur connecté
        visitors = visitors.filter(v => v.id !== parseInt(userId));
        setVisitors(visitors);
      }

      if (photosResult.status === 'fulfilled' && photosResult.value.success) {
        setRecentPhotos(photosResult.value.data);
      }

    } catch (err) {
      console.error('Erreur chargement dashboard:', err);
      setError('Impossible de charger les données');
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  }, [userId, user?.genre]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // ✅ CORRECTION ICI : Vérification que l'utilisateur ne like pas lui-même
const handleLike = async (targetUserId) => {
  // Vérification côté client
  if (parseInt(userId) === parseInt(targetUserId)) {
    toast.error('Vous ne pouvez pas liker votre propre profil');
    return false;
  }

  // Sauvegarder l'état actuel pour le rollback
  const currentMatch = hotMatches.find(m => m.id === targetUserId);
  const wasLiked = currentMatch?.already_liked || false;

  // Optimistic update
  setHotMatches(prev => 
    prev.map(match => 
      match.id === targetUserId 
        ? { ...match, already_liked: !wasLiked } 
        : match
    )
  );

  try {
    const result = await dashboardService.likeUser(targetUserId);
    
    if (result.success) {
      // 🔥 CORRECTION ICI : Utiliser result.action pour déterminer le message
      if (result.action === 'liked') {
        if (result.match) {
          toast.success('C\'est un match ! 🔥', { duration: 5000 });
        } else {
          toast.success('Like envoyé !');
        }
      } else if (result.action === 'unliked') {
        toast.success('Like retiré');
      } else {
        // Fallback
        toast.success(result.message || 'Action effectuée');
      }
      
      // Mettre à jour avec le vrai statut de l'API
      setHotMatches(prev => 
        prev.map(match => 
          match.id === targetUserId 
            ? { ...match, already_liked: result.action === 'liked' } 
            : match
        )
      );
      
      // Si c'est un match, incrémenter le compteur
      if (result.match) {
        setStats(prev => ({
          ...prev,
          matches: prev.matches + 1
        }));
      }
      
      return true;
    } else {
      // Rollback en cas d'erreur
      setHotMatches(prev => 
        prev.map(match => 
          match.id === targetUserId 
            ? { ...match, already_liked: wasLiked } 
            : match
        )
      );
      
      if (result.error === 'already_liked') {
        toast('Vous avez déjà liké ce profil', { icon: '💔' });
      } else {
        toast.error(result.error || 'Erreur lors du like');
      }
      return false;
    }
  } catch (error) {
    // Rollback en cas d'erreur réseau
    setHotMatches(prev => 
      prev.map(match => 
        match.id === targetUserId 
          ? { ...match, already_liked: wasLiked } 
          : match
      )
    );
    console.error('Erreur like:', error);
    toast.error('Erreur réseau');
    return false;
  }
};

  const refreshData = () => {
    loadDashboardData();
  };

  return {
    loading,
    stats,
    hotMatches,
    suggestions,
    visitors,
    recentPhotos,
    error,
    handleLike,
    refreshData
  };
};